import { TypographyComponent as Typography } from '@tvg/fuse/typography';
import { SrOnly } from '@tvg/fuse/utils/sr-only';
import classNames from 'classnames';
import { PLPQueryData, usePLPQuery } from 'client/api/plp-results';
import { DEFAULT_PRODUCTS_PER_PAGE } from 'common/config';
import React, { useCallback, useMemo } from 'react';
import styles from './ResultCount.module.css';

type ResultCountProps = {
  position: string;
};

function ResultCount({ position }: ResultCountProps) {
  const { data } = usePLPQuery(
    useCallback(
      (d: PLPQueryData) => ({
        numResults: d.numResults,
        resultsPerPage: d.resultsPerPage,
        page: d.page,
      }),
      [],
    ),
  );

  const numResults = data?.numResults ?? 0;
  const page = data?.page ?? 1;
  const resultsPerPage = data?.resultsPerPage ?? DEFAULT_PRODUCTS_PER_PAGE;

  const id = `results-header-${position}`;
  const isTopComponent = position === 'top';

  const text = useMemo(() => {
    if (numResults === undefined || page === undefined || resultsPerPage === undefined) {
      return { resultCount: 'Results:', screenReader: '' };
    }

    const startProductNumber = 1 + (page - 1) * resultsPerPage;
    const endProductNumber = page * resultsPerPage;
    const endProduct = endProductNumber < numResults ? endProductNumber : numResults;
    const displayedResultCount: string | number | undefined = numResults <= 9999 ? numResults : '10k+';

    const resultCountText = `Results: ${startProductNumber} - ${endProduct} (of ${displayedResultCount})`;
    return {
      resultCount: resultCountText,
      screenReader: `Page ${page}. ${resultCountText.replace('-', 'to')}`,
    };
  }, [numResults, page, resultsPerPage]);

  const textSizes = useMemo(() => {
    if (isTopComponent) return { xxs: '1', m: '2', l: '4' };
    return { xxs: '1', m: '1', l: '1' };
  }, [isTopComponent]);

  return (
    <div
      id={id}
      aria-live={isTopComponent ? 'polite' : 'off'}
      role={isTopComponent ? 'alert' : undefined}
      className={classNames(styles.resultCount, { [styles['resultCount--bottom']]: position === 'bottom' })}
    >
      <SrOnly>{text.screenReader}</SrOnly>
      <Typography dataTestId="results-count" tag="h2" size={textSizes} type="heading" noWrap aria-hidden>
        {text.resultCount}
      </Typography>
    </div>
  );
}

export default React.memo(ResultCount);
